import {
  Auth0Client,
  GenericError,
  MissingRefreshTokenError,
  TimeoutError,
} from '@auth0/auth0-spa-js';

export const AUTH0_DOMAIN = 'auth.rypt.dev';

/**
 * Hardcoded, deliberately not an environment variable.
 *
 * Without this audience Auth0 issues an OPAQUE token instead of a JWT, rypt refuses it,
 * and every call 401s — which the API's own README calls the single most common way to
 * get a 401 from a setup that otherwise looks right. An env var is precisely how a
 * deployment gets configured into that loop, so there is nothing to misconfigure here.
 */
export const API_AUDIENCE = 'https://api.rypt.dev';

export const CALLBACK_PATH = '/callback';

export const auth0 = new Auth0Client({
  domain: AUTH0_DOMAIN,
  clientId: import.meta.env.VITE_AUTH0_CLIENT_ID,
  authorizationParams: {
    redirect_uri: `${window.location.origin}${CALLBACK_PATH}`,
    audience: API_AUDIENCE,
    scope: 'openid profile email', // the SDK adds offline_access when useRefreshTokens is on
  },
  // Tokens live in memory, never in localStorage: a refresh token in storage is readable
  // by any XSS. The cost is that a hard reload loses it and the session is rebuilt --
  // through the silent iframe where third-party cookies still work, and through one
  // full-page redirect where they do not (Safari, Firefox). That bounce is silent while
  // the Auth0 session cookie lives.
  cacheLocation: 'memory',
  useRefreshTokens: true,
  // Defaults to FALSE. Without it there is no iframe fallback at all and every reload is
  // a redirect. Verified against the 2.27.0 typings, not assumed.
  useRefreshTokensFallback: true,
});

/** A JWT has three segments. An opaque Auth0 token does not. */
export const isJwtShaped = (t: string): boolean => t.split('.').length === 3;

/**
 * Decoded, never verified — and it runs in production, not just in dev.
 *
 * Turning a silent redirect loop into a page that says which setting is wrong is a
 * usability operation, not a security one: the API verifies the signature, and nothing
 * here grants anything.
 */
export function tokenAudienceMatches(token: string): boolean {
  try {
    const payload = token.split('.')[1];
    if (!payload) return true;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const claims = JSON.parse(json) as { aud?: string | string[] };
    const aud = Array.isArray(claims.aud) ? claims.aud : [claims.aud];
    return aud.includes(API_AUDIENCE);
  } catch {
    return true; // undecodable: let the API be the judge
  }
}

/** Whether a silent-renewal failure actually means "the user must interact". */
export function isInteractionRequired(cause: unknown): boolean {
  if (cause instanceof MissingRefreshTokenError) return true;
  if (cause instanceof TimeoutError) return false; // a slow network is not a dead session
  if (cause instanceof GenericError) {
    return [
      'login_required',
      'consent_required',
      'interaction_required',
      'mfa_required',
      'invalid_grant',
      'access_denied',
    ].includes(cause.error);
  }
  return false;
}

export function currentReturnTo(): string {
  const { pathname, search, hash } = window.location;
  return pathname.startsWith(CALLBACK_PATH) ? '/overview' : `${pathname}${search}${hash}`;
}

/* -------- single-flight, redirect-surviving re-auth --------
 *
 * The breaker lives in sessionStorage, not in a module variable. A module `let` is
 * destroyed by the very redirect it is supposed to guard, so it de-duplicates concurrent
 * 401s within one page load and protects nothing across navigations — which is exactly
 * the case that loops.
 */
const BREAKER_KEY = 'rypt.reauth';
const BREAKER_MS = 60_000;

let inFlight = false;
let clearCaches: () => void = () => {};

/** Wire up cache teardown; called once from queryClient.ts. */
export const onReauth = (fn: () => void): void => {
  clearCaches = fn;
};

function breakerTripped(): boolean {
  try {
    const at = Number(sessionStorage.getItem(BREAKER_KEY) ?? 0);
    return Date.now() - at < BREAKER_MS;
  } catch {
    return false;
  }
}

export const reauthBlocked = (): boolean => breakerTripped();

/** At most one redirect per minute, across page loads. False means the breaker refused. */
export function beginReauth(returnTo: string = currentReturnTo()): boolean {
  if (inFlight) return true;
  if (breakerTripped()) return false;
  inFlight = true;
  try {
    sessionStorage.setItem(BREAKER_KEY, String(Date.now()));
  } catch {
    /* best effort */
  }
  clearCaches();
  void auth0.loginWithRedirect({ appState: { returnTo } });
  return true;
}

/** The user's own click on "Sign in again": clear the breaker and go. */
export function forceReauth(): void {
  try {
    sessionStorage.removeItem(BREAKER_KEY);
  } catch {
    /* best effort */
  }
  clearCaches();
  void auth0.loginWithRedirect({ appState: { returnTo: currentReturnTo() } });
}

export async function signOut(): Promise<void> {
  // The query cache is per-tab and would otherwise show one tenant's keys to the next
  // person who signs in on this machine.
  clearCaches();
  await auth0.logout({ logoutParams: { returnTo: window.location.origin } });
}
